/**
 * Polish display copy — the source of truth, taken verbatim from the approved
 * "MB Group 6" mock-up except where a deliberate modification was requested:
 *
 *   navigatorConsult — the service catalogue's category CTA now reads
 *   "Umów konsultację" instead of the mock-up's "Zobacz usługi w kategorii".
 */
import type { SiteCopy } from "./copy.js";

export const plCopy: SiteCopy = {
	brand: {
		name: "MB Group",
		tagline: "The Circular ECOsystem",
		quote: "„Tworzymy przyszłość obiegu zamkniętego”",
		blurb:
			"The Circular ECOsystem — recykling, ponowne użycie, zbiórka, system kaucyjny i ESG w jednym ekosystemie usług.",
		mockupNote: "Makieta koncepcyjna · lipiec 2026",
	},
	nav: {
		services: "Usługi",
		industries: "Branże",
		cases: "Realizacje",
		knowledge: "Wiedza",
		about: "O Grupie",
		contact: "Kontakt",
		consult: "Umów konsultację",
		search: "Szukaj",
		menu: "Menu",
		close: "Zamknij",
		megaEyebrow: "Usługi wg kategorii — nie wg spółek",
		fullPortfolio: "Pełne portfolio usług →",
		start: "Start",
	},
	home: {
		heroEyebrow: "MB Group · Circular ECOsystem",
		heroTitle: "Jedna grupa. Pełny ekosystem usług cyrkularnych.",
		heroLead:
			"Recykling, ponowne użycie, tekstylia, logistyka zbiórki, system kaucyjny i ESG — poukładane w kategorie usług, nie w strukturę spółek. Ty wybierasz efekt, my dobieramy zasoby.",
		heroSearchPlaceholder: "Wpisz usługę, której szukasz lub opisz problem…",
		heroCtaPrimary: "Znajdź rozwiązanie",
		heroCtaSecondary: "Przeglądaj usługi",
		heroStats: [
			{ value: "9", label: "spółek w jednym ekosystemie" },
			{ value: "8", label: "kategorii usług do wyboru" },
			{ value: "1", label: "umowa i jeden opiekun" },
		],
		heroFigureCaption: "Obieg zamknięty w MB Group — od zbiórki po surowiec i raport.",
		heroFigureAlt:
			"Diagram gospodarki o obiegu zamkniętym: zbiórka, sortowanie, przetwarzanie, surowiec wtórny, ponowne użycie i raportowanie połączone w zamkniętą pętlę.",
		ticker: [
			"Baterie Li-ion",
			"ZSEE",
			"Chłodnictwo",
			"PCB i elektronika",
			"Tekstylia",
			"REUSE",
			"System kaucyjny",
			"ROP",
			"Transport ADR",
			"Ewidencja BDO",
			"Raport CSRD",
			"Ślad węglowy",
		],
		videoEyebrow: "Think Tank MB Group",
		videoTitle: "Regulacje zmieniają się szybciej niż Twój budżet.",
		videoLead:
			"System kaucyjny, ROP dla tekstyliów, rozporządzenie bateryjne, CSRD. Nasz think tank przekłada nowe przepisy na konkretne decyzje operacyjne — zanim staną się kosztem.",
		videoStats: [
			{ value: "4 obszary", label: "kaucja · ROP · baterie · ESG" },
			{ value: "Bez żargonu", label: "wnioski, nie cytaty z ustawy" },
		],
		videoPlay: "Odtwórz film",
		videoPause: "Pauza",
		videoMuteOn: "Włącz dźwięk",
		videoMuteOff: "Wycisz",
		videoMore: "Więcej analiz →",
		navigatorEyebrow: "Usługi, nie spółki",
		navigatorTitle: "Nawigujesz przez potrzeby, nie przez strukturę grupy.",
		navigatorLead:
			"Za każdą usługą stoi wyspecjalizowana spółka i realna infrastruktura. Ty wybierasz efekt biznesowy — my dobieramy zasoby całego ekosystemu MB Group.",
		navigatorBadge: "Kategoria",
		navigatorConsult: "Umów konsultację →",
		navigatorStatLabel: "usług w tej kategorii — jedna umowa, jeden opiekun",
		proofEyebrow: "Realna infrastruktura",
		proofTitle: "Za każdą usługą stoi zakład, flota i zespół — nie prezentacja.",
		proofLead:
			"Własne zakłady przetwarzania, sortownia tekstyliów, transport ADR, sieć punktów zbiórki i zespoły compliance. Obsługujemy producentów, retail, samorządy i instytucje w całej Polsce oraz na rynkach eksportowych.",
		proofFacts: [
			{ value: "7 zakładów", label: "przetwarzania i sortowania" },
			{ value: "ADR", label: "własna flota do baterii Li-ion" },
			{ value: "Ogólnopolska", label: "sieć zbiórki i punktów zwrotu" },
			{ value: "CSRD", label: "raportowanie wg ESRS" },
		],
		industriesTitle: "Rozwiązania dla Twojej branży",
		industriesAll: "Wszystkie branże →",
		magnetsEyebrow: "Materiały eksperckie",
		magnetsTitle: "Wiedza, którą możesz od razu wykorzystać.",
		magnetsLead:
			"Raporty, webinary i case studies przygotowane przez zespoły operacyjne — nie przez dział marketingu.",
		magnetsAll: "Cała baza wiedzy →",
		ctaEyebrow: "Szybki kontakt",
		ctaTitle: "Opisz problem — dobierzemy usługi z całego ekosystemu.",
		ctaLead:
			"Jeden kontakt, jedna oferta, komplet dokumentacji. Nie musisz wiedzieć, która spółka grupy odpowiada za Twój temat.",
		ctaFacts: [
			{ value: "24 h", label: "czas odpowiedzi w dni robocze" },
			{ value: "1 opiekun", label: "dla wszystkich usług z grupy" },
		],
	},
	newsletter: {
		title: "Newsletter regulacyjny MB Group",
		desc: "Raz w miesiącu: co się zmieniło w przepisach i co to znaczy dla Twojej firmy. Bez ofert sprzedażowych.",
		emailPlaceholder: "E-mail służbowy",
		submit: "Zapisz się",
		doneTitle: "Zapisano do newslettera",
		doneDesc:
			"Na podany adres wysłaliśmy e-mail potwierdzający subskrypcję. Możesz wypisać się jednym kliknięciem w każdej wiadomości.",
		kind: "Newsletter",
		tag: "Newsletter regulacyjny",
	},
	quickForm: {
		title: "Oddzwonimy do Ciebie",
		desc: "Zostaw numer i wybierz dogodną porę — bez formularzy na pół strony.",
		name: "Imię i nazwisko",
		phone: "Telefon",
		slotLabel: "Preferowana pora kontaktu",
		slots: ["Rano 8–11", "Południe 11–14", "Popołudnie 14–17", "Dowolna"],
		submit: "Zamów kontakt",
		mailInstead: "Wolisz opisać sprawę mailem? Przejdź do formularza →",
		sentTitle: "Zgłoszenie przyjęte",
		sentDesc: "Opiekun kategorii oddzwoni w wybranym przedziale.",
		sendAnother: "Wyślij kolejne",
	},
	services: {
		title: "Pełne portfolio usług — ułożone w osiem kategorii.",
		lead: "Bez nazw spółek i skrótów wewnętrznych. Wybierasz usługę, my podstawiamy właściwy zakład, flotę i zespół z grupy.",
		viewService: "Zobacz usługę →",
		inCategory: "usług w kategorii",
	},
	serviceDetail: {
		ask: "Zapytaj o tę usługę →",
		allServices: "Wszystkie usługi",
		scopeEyebrow: "Zakres i specjalizacje",
		scopeTitle: "Co dokładnie realizujemy",
		groupsEyebrow: "Grupy produktowe",
		groupsTitle: "Co przyjmujemy i obsługujemy",
		notListedTitle: "Nie widzisz swojego strumienia?",
		notListedDesc:
			"Obsługujemy również strumienie mieszane i nietypowe — opisz swój przypadek, przygotujemy dobór usług.",
		notListedCta: "Opisz przypadek →",
		processEyebrow: "Jak przebiega współpraca",
		processTitle: "Cztery kroki od zgłoszenia do dokumentacji",
		process: [
			{
				n: "01",
				title: "Zgłoszenie i analiza",
				desc: "Opisujesz strumień lub cel — wracamy z doborem usług i pytaniami uzupełniającymi.",
			},
			{
				n: "02",
				title: "Oferta i umowa",
				desc: "Wycena, warunki logistyczne i zakres dokumentacji w jednym dokumencie.",
			},
			{
				n: "03",
				title: "Realizacja",
				desc: "Odbiór, przetwarzanie lub wdrożenie zgodnie z ustalonym harmonogramem.",
			},
			{
				n: "04",
				title: "Dokumentacja i raport",
				desc: "Komplet dokumentów oraz zestawienie mas i efektów do Twojej sprawozdawczości.",
			},
		],
		relatedTitle: "Pozostałe usługi w kategorii",
		relatedAll: "Pełne portfolio →",
	},
	about: {
		eyebrow: "MB Group · The Circular ECOsystem",
		title: "Grupa, która zamyka obieg — od zbiórki po surowiec i raport.",
		lead: "Materiał wideo z targów branżowych — zespoły, zakłady i rozmowy, które stoją za każdą usługą w portfolio.",
		bodyTitle: "Jedna grupa, dziewięć wyspecjalizowanych organizacji.",
		bodyParagraphs: [
			"Zakłady przetwarzania ZSEE, baterii i elektroniki, sortownia tekstyliów, operacje systemu kaucyjnego, sieci zbiórki, handel REUSE oraz doradztwo ESG. Klient nie musi znać naszej struktury — wystarczy, że wskaże potrzebę.",
			"Działamy w Polsce i na rynkach eksportowych, obsługując producentów, sieci handlowe, samorządy, instytucje i gospodarstwa domowe.",
		],
		pillarsTitle: "Filary grupy",
	},
	industries: {
		title: "Rozwiązania dopasowane do specyfiki branży.",
		lead: "Ten sam ekosystem usług, inne priorytety: zgodność, koszt, ryzyko pożarowe, wizerunek albo odzysk wartości.",
	},
	cases: {
		title: "Wdrożenia, które da się policzyć.",
		lead: "Konkretne projekty z wynikiem liczbowym — audyt, wdrożenie i efekt po pierwszym roku.",
	},
	knowledge: {
		title: "Regulacje, terminy i praktyka — bez prawniczego żargonu.",
		lead: "Analizy przygotowane przez zespoły operacyjne i doradcze grupy.",
		read: "Czytaj analizę →",
	},
	article: {
		ctaTitle: "Chcesz omówić to na swoim przypadku?",
		ctaDesc: "Umów rozmowę z opiekunem kategorii albo pobierz pełny raport na ten temat.",
		ctaConsult: "Umów konsultację",
		ctaBack: "Wróć do bazy wiedzy",
	},
	contact: {
		title: "Opisz problem lub cel biznesowy.",
		lead: "Nie musisz wiedzieć, która spółka grupy odpowiada za Twój temat. Wystarczy opis sytuacji — wrócimy z doborem usług i wyceną w 24 h roboczych.",
		points: [
			"Odpowiedź w 24 h roboczych od zgłoszenia",
			"Jeden opiekun dla wszystkich usług z grupy",
			"Komplet dokumentacji odzysku i sprawozdawczości",
			"Obsługa klientów wielolokalizacyjnych w całej Polsce",
		],
		formTitle: "Formularz kontaktowy",
		name: "Imię i nazwisko",
		company: "Firma / instytucja",
		email: "E-mail służbowy",
		categoryPlaceholder: "Wybierz kategorię usług…",
		message: "Opisz problem, strumień odpadu lub cel biznesowy…",
		submit: "Wyślij zgłoszenie",
		privacyNote:
			"Wysyłając formularz akceptujesz politykę prywatności. Makieta demo — dane nie są zapisywane.",
		sentTitle: "Dziękujemy za zgłoszenie",
		sentDesc: "Zgłoszenie trafiło do opiekuna właściwej kategorii usług.",
		sendAnother: "Wyślij kolejne",
	},
	assistant: {
		title: "Asystent MB Group",
		subtitle: "Opisz problem — dobierzemy usługi z całego ekosystemu",
		placeholder: "np. mam 200 kg baterii z hulajnóg — co dalej?",
		frequent: "Częste pytania:",
		prompts: [
			"mam 200 kg baterii z hulajnóg",
			"obowiązki ROP dla tekstyliów",
			"zwroty konsumenckie AGD",
			"raport CSRD dla spółki",
			"lodówki z wymiany sprzętu",
		],
		recommendation: "Rekomendacja asystenta",
		answerFound:
			"Na podstawie opisu proponujemy zacząć od: „{service}”. Zapytanie zostało otagowane kategorią „{category}” — jeśli zostawisz kontakt, opiekun tej kategorii odezwie się w ciągu 24 h roboczych.",
		answerEmpty:
			"Nie mamy gotowej usługi pod to zapytanie, ale obsługujemy strumienie mieszane i nietypowe. Zostaw opis przypadku — wrócimy z rekomendacją w 24 h roboczych.",
		privacyNote:
			"Zapytania są anonimowo zbierane jako baza wiedzy o potrzebach klientów i tagowane do właściwej kategorii usług.",
		expertCta: "Porozmawiaj z ekspertem →",
		openLabel: "Asystent AI",
		resultsLabel: "Dopasowane usługi",
	},
	gate: {
		email: "E-mail służbowy",
		company: "Firma",
		privacyNote:
			"Zapisując się akceptujesz politykę prywatności. Makieta demo — dane nie są zapisywane.",
		close: "Zamknij",
	},
	resources: {
		title: "Raporty i materiały",
		webinarsTitle: "Webinary",
		upcoming: "Nadchodzący",
		recording: "Nagranie",
		noWebinars: "Obecnie nie mamy zaplanowanych webinarów.",
	},
	search: {
		emptyHint: "Wpisz frazę, aby przeszukać serwis.",
		resultsCount: "Wyników dla „{query}”: {total}",
	},
	preview: {
		title: "Podgląd niedostępny",
		desc: "Podgląd wersji roboczych jest dostępny wyłącznie dla zalogowanych redaktorów.",
		signIn: "Zaloguj się",
		forbidden: "Twoje konto nie ma uprawnień do podglądu wersji roboczych.",
	},
	panel: {
		title: "Panel redaktora",
		signInLead:
			"Zaloguj się kontem CMS (Strapi). Te same dane otwierają panel treści i podgląd wersji roboczych na stronie.",
		email: "E-mail",
		password: "Hasło",
		submit: "Zaloguj się",
		submitting: "Logowanie…",
		errorInvalid: "Nieprawidłowy e-mail lub hasło.",
		errorRateLimited: "Zbyt wiele prób logowania. Spróbuj ponownie za kilka minut.",
		errorUnavailable: "Logowanie jest chwilowo niedostępne. Spróbuj ponownie za chwilę.",
		signedInAs: "Zalogowano jako",
		rolesTitle: "Twoje role",
		noEditorRole:
			"Ta rola nie obejmuje podglądu wersji roboczych na stronie (ma go redaktor, zatwierdzający i administratorzy). Treści edytujesz w CMS.",
		cmsTitle: "Treści w CMS",
		cmsDesc:
			"Edycja usług, artykułów, realizacji i ustawień serwisu. Zaloguj się tymi samymi danymi.",
		cmsCta: "Otwórz CMS",
		previewTitle: "Podgląd wersji roboczych",
		previewDesc:
			"Zmiany zapisane w CMS, ale jeszcze nieopublikowane, zobaczysz pod adresem /podglad/<typ>/<slug>, np. /podglad/service/<slug-usługi>. Typy: service, company, case-study, article.",
		signOut: "Wyloguj się",
		roleNames: {
			group_admin: "Administrator grupy",
			domain_admin: "Administrator serwisu",
			domain_editor: "Redaktor",
			content_approver: "Zatwierdzający treści",
			translator: "Tłumacz",
			marketing: "Marketing",
			sales: "Sprzedaż",
			analyst: "Analityk",
		},
	},
	footer: {
		servicesTitle: "Usługi",
		groupTitle: "Grupa",
		contactTitle: "Kontakt",
		locations: "Micigózd · Głogów Małopolski\nStąporków · Sztum · Kielce\nWarszawa",
		rights: "Wszystkie prawa zastrzeżone.",
	},
	status: {
		notFoundTitle: "Nie znaleźliśmy tej strony",
		notFoundDesc:
			"Adres mógł się zmienić albo materiał został przeniesiony. Zajrzyj do pełnego portfolio usług.",
		notFoundCta: "Przejdź do usług",
		errorTitle: "Coś poszło nie tak",
		errorDesc: "Nie udało się wczytać tej sekcji. Spróbuj ponownie.",
		retry: "Spróbuj ponownie",
	},
	demo: {
		bannerTitle: "Tryb demonstracyjny",
		bannerBody:
			"System zarządzania treścią jest chwilowo nieosiągalny — pokazujemy zapisaną wersję katalogu usług. Dane z formularzy nie są w tym trybie zapisywane.",
	},
	a11y: {
		mainNav: "Nawigacja główna",
		breadcrumb: "Ścieżka nawigacji",
		servicesMenu: "Kategorie usług",
	},
};
